import React, { useMemo } from 'react';
import { Fleet, GameState, ShipInstance, StarSystem } from '../types';

type BattleViewerProps = {
  visible: boolean;
  systemId: number | null;
  gameState: GameState;
  onClose: () => void;
};

function ShipRow({ ship }: { ship: ShipInstance }) {
  const pct = ship.maxHp > 0 ? Math.max(0, Math.min(100, (ship.hp / ship.maxHp) * 100)) : 0;
  const color = pct > 60 ? '#00c853' : pct > 30 ? '#ffb300' : '#ff5252';
  const icon = ship.class === 'frigate' ? '⚔️' : ship.class === 'destroyer' ? '🛡️' : ship.class === 'cruiser' ? '🛰️' : '👑';
  const label = ship.class.charAt(0).toUpperCase() + ship.class.slice(1);
  return (
    <div className="row between" style={{ alignItems: 'center', gap: 8 }}>
      <div style={{ minWidth: 120, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span aria-hidden>{icon}</span>
        <span style={{ opacity: 0.9 }}>{label}</span>
        <span style={{ opacity: 0.7, fontSize: 12 }}>#{ship.id}</span>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ height: 8, background: '#333', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color }} />
        </div>
      </div>
      <div style={{ minWidth: 70, textAlign: 'right', opacity: 0.9 }}>{Math.round(ship.hp)}/{Math.round(ship.maxHp)}</div>
    </div>
  );
}

function FleetBlock({ fleet, title, ownerName }: { fleet: Fleet; title?: string; ownerName?: string }) {
  const ships = (fleet.ships || []).slice().sort((a, b) => {
    const order: Record<string, number> = { frigate: 0, destroyer: 1, cruiser: 2, capital: 3 };
    const oa = order[a.class] ?? 0; const ob = order[b.class] ?? 0;
    if (oa !== ob) return oa - ob;
    return a.id - b.id;
  });
  return (
    <div className="card">
      <div className="label">{title || `Fleet F${fleet.id}`}</div>
      <div className="row between" style={{ marginBottom: 6 }}>
        <div style={{ opacity: 0.9 }}>Owner: {ownerName || fleet.owner}</div>
        <div className="health">{Math.round(fleet.power)}/{Math.round(fleet.maxPower)}</div>
      </div>
      <div className="stack" style={{ maxHeight: 220, overflowY: 'auto' }}>
        {ships.map(s => <ShipRow key={`${s.class}-${s.id}`} ship={s} />)}
        {ships.length === 0 && <div style={{ opacity: 0.75 }}>No ships</div>}
      </div>
    </div>
  );
}

export default function BattleViewer({ visible, systemId, gameState, onClose }: BattleViewerProps) {
  const system: StarSystem | null = useMemo(() => {
    if (!visible || systemId == null) return null;
    return gameState.systems.find(s => s.id === systemId) || null;
  }, [visible, systemId, gameState.systems]);

  const { attackerFleets, defenderFleets, round } = useMemo(() => {
    if (!system) return { attackerFleets: [] as Fleet[], defenderFleets: [] as Fleet[], round: null as number | null };
    const inZone = gameState.fleets.filter(f => f.location === system.id && f.inCombat);
    const attackers = inZone.filter(f => (f as any).isOriginalAttacker === true);
    const defenders = inZone.filter(f => (f as any).isOriginalAttacker === false);
    const r = Math.max(0, ...inZone.map(f => (f as any).combatRound ?? 0)) || null;
    return { attackerFleets: attackers, defenderFleets: defenders, round: r };
  }, [system, gameState.fleets]);

  if (!visible || !system) return null;

  const battleOngoing = attackerFleets.length + defenderFleets.length > 0;

  return (
    <div className="screen-overlay" onClick={onClose}>
      <div className="screen-panel" role="dialog" aria-modal="true" aria-label={`Battle at ${system.name}`} onClick={(e) => e.stopPropagation()}>
        <div className="screen-header">
          <div className="screen-title">Battle at {system.name}</div>
          <button className="close-btn" onClick={onClose} title="Close">✕</button>
        </div>
        <div className="screen-content">
          {!battleOngoing ? (
            <div style={{ opacity: 0.85 }}>No active combat here.</div>
          ) : (
            <div className="stack" style={{ gap: 12 }}>
              <div className="row between" style={{ alignItems: 'baseline' }}>
                <div className="pill">Round {round ?? '—'}</div>
                <div style={{ opacity: 0.85 }}>Tick t{gameState.tick}</div>
              </div>
              <div className="row" style={{ gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div className="card"><div className="label">Attacker</div></div>
                  {attackerFleets.map(f => (
                    <FleetBlock key={f.id} fleet={f} title={`F${f.id}`} ownerName={gameState.players.find(p => p.id === f.owner)?.name || (f.owner as any)} />
                  ))}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="card"><div className="label">Defender</div></div>
                  {defenderFleets.map(f => (
                    <FleetBlock key={f.id} fleet={f} title={`F${f.id}`} ownerName={gameState.players.find(p => p.id === f.owner)?.name || (f.owner as any)} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


